import React, { useState, useEffect, useRef } from 'react';
import '../styles/myRequests.css';
import {
    IconButton, TooltipHost, Dialog, DialogFooter, Spinner, Separator, DefaultButton,
    mergeStyleSets, PrimaryButton, DialogType, Label, SpinnerSize
} from '@fluentui/react';
import { initializeIcons } from '@fluentui/react/lib/Icons';
import { useApiService } from '../services/apiServices';
import AdvancedSearch from '../components/advancedSearch';
import clearicon from '../assets/closecircle.svg';
import emptybackground from '../assets/nofile.png';
import useAffidavitService from '../services/affidavitServices';
import { useBoolean } from '@fluentui/react-hooks';
import { toast } from "react-toastify";
import { Refresh } from "@mui/icons-material";
import { ContextualMenu } from '@fluentui/react/lib/ContextualMenu';
import { mergeStyles } from '@fluentui/react';
import { Pagination } from '../components/Pagination';

initializeIcons();

const ITEMS_PER_PAGE = 20;

const RequestsList = ({ courts, courtDivisions, loading, setLoading }) => {
    const handleApiRequest = useApiService();

    const searchInputRef = useRef(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [documentIdToCancel, setDocumentIdToCancel] = useState(null);

    const [isDraggable] = useBoolean(true);
    const [isVisible, { setTrue: showDialog, setFalse: hideDialog }] = useBoolean(false);

    const [allAffidavitRequests, setAllAffidavitRequests] = useState([]);
    const [filteredAffidavitRequests, setFilteredAffidavitRequests] = useState([]);
    const [courtDivisionsForCourt, setCourtDivisionsForCourt] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [selectedCourtId, setSelectedCourtId] = useState("");
    const [selectedCourtDivisionId, setSelectedCourtDivisionId] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [tableHeight, setTableHeight] = useState('auto');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentDocumentUrl, setCurrentDocumentUrl] = useState('');
    const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [searchCriteria, setSearchCriteria] = useState(null);

    const { fetchMyAffidavitData, fetchAffidavitByFilter } = useAffidavitService();

    const defaultMessage = (
        <>
            No pending requests found. Would you like to create a new request? Click{' '}
            <a href='/apply-affidavit'>here</a> to apply.
        </>
    );

    const searchBoxStyles = {
        container: {
            display: "flex",
            alignItems: "center",
            border: "1px solid #0A722F",
            borderRadius: "10px",
            overflow: "hidden",
            width: "250px",
            marginTop: "auto"
        },
        input: {
            flex: 1,
            border: "none",
            outline: "none",
            padding: "10px 15px",
            fontSize: "14px",
            color: "#333",
            "::placeholder": { color: "#B0B0B0" },
        },
        button: {
            backgroundColor: "#0A722F",
            height: "100%",
            color: "#fff",
            padding: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "15%",
            cursor: "pointer",
        },
    };

    const classNames = mergeStyleSets({
        customButtonColor: {
            backgroundColor: '#004d00',
            border: 'none',
            color: '#ffffff',
            selectors: {
                ':hover': { backgroundColor: '#003300' },
                ':focus': {
                    backgroundColor: '#002200',
                    outline: 'none',
                    boxShadow: '0 0 0 2px #ffffff',
                },
            },
        },
        buttonSpacing: { marginRight: '20px' },
    });

    const dragOptions = {
        moveMenuItemText: 'Move',
        closeMenuItemText: 'Close',
        menu: ContextualMenu,
        keepInBounds: true,
    };

    const dialogStyles = mergeStyles({
        main: {
            width: '90%',
            maxWidth: '800px',
            '@media (max-width: 768px)': { width: '100%', padding: '10px' },
        },
        content: {
            '@media (max-width: 768px)': { padding: '10px' },
        },
    });

    // ─── Resize handler ───────────────────────────────────────────────────────
    useEffect(() => {
        const handleResize = () => setTableHeight('auto');
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);


        // ─── Initial, page-change, and search fetch ──────────────────────────────
    useEffect(() => {
        loadData(currentPage, searchCriteria);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, searchCriteria]);
 
    const loadData = async (page = 1, criteria = null) => {
        try {
            setLoading(true);
            setMessage("");
 
            let response;
            if (criteria) {
                response = await fetchAffidavitByFilter(criteria, page, ITEMS_PER_PAGE);
            } else {
  
                response = await fetchMyAffidavitData(page, ITEMS_PER_PAGE);
            }
            const unsigned = (response?.data ?? []).filter((request) => !request.signedDocumentUrl);
            if (unsigned.length > 0) {
                setAllAffidavitRequests(unsigned);
                setFilteredAffidavitRequests(unsigned);
                setTotalCount(response.totalCount);
            } else {
                setAllAffidavitRequests([]);
                setFilteredAffidavitRequests([]);
                setTotalCount(0);
                setMessage(criteria
                    ? "Your search did not return any results. Please check your search criteria and try again."
                    : defaultMessage);
            }
        } catch (error) {
            console.error("Failed to fetch data:", error);
            setMessage("An error occurred while processing your data. Please try again later.");
        } finally {
            setLoading(false);
        }
    };


    // ─── Refresh ──────────────────────────────────────────────────────────────
    const handleRefresh = async () => {
        setSearchText("");
        setSelectedCourtId("");
        setSelectedCourtDivisionId("");
        setCourtDivisionsForCourt([]);
        if (searchInputRef.current) searchInputRef.current.value = "";
 
        // Return to browse mode. If already on page 1 with no criteria, neither state
        // changes, so reload explicitly; otherwise the effect handles it.
        const alreadyBrowsingPage1 = currentPage === 1 && searchCriteria === null;
        setSearchCriteria(null);
        setCurrentPage(1);
        if (alreadyBrowsingPage1) {
            await loadData(1, null);
        }
    };

    // ─── Modal ────────────────────────────────────────────────────────────────
    const handleDownload = (documentUrl) => {
        setCurrentDocumentUrl(documentUrl);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setCurrentDocumentUrl('');
    };

    // ─── Client-side filtering triggers server-side filtering by setting criteria state ───────────────
    const applyServerFilter = (courtId, courtDivisionId, searchValue) => {
        const criteria = {};

        if (courtId && parseInt(courtId) !== 0) {
            const court = courts.find((c) => c.id === parseInt(courtId));
            if (court) criteria.courtName = court.courtName;
        }

        if (courtDivisionId && parseInt(courtDivisionId) !== 0) {
            const division = courtDivisions.find((d) => d.id === parseInt(courtDivisionId));
            if (division) criteria.courtDivision = division.courtDivisionName;
        }

        if (searchValue) criteria.documentId = searchValue;


        const hasCriteria = Object.keys(criteria).length > 0;

        setSearchCriteria(hasCriteria ? criteria : null);
        setCurrentPage(1);
    };

    const handleSearchTextChange = () => {
        const searchValue = searchInputRef.current.value;
        setSearchText(searchValue);
        applyServerFilter(selectedCourtId, selectedCourtDivisionId, searchValue);
    };

    const handleCourtDropdownChange = (event) => {
        const courtId = event.target.value;
        setSelectedCourtDivisionId("");
        setSelectedCourtId(courtId);

        if (courtId === "0") {
            setCourtDivisionsForCourt([{ id: 0, courtDivisionName: "All" }]);
        } else {
            const divisionsForCourt = courtDivisions.filter(
                (division) => division.courtId === parseInt(courtId)
            );
            divisionsForCourt.unshift({ id: 0, courtDivisionName: "All" });
            setCourtDivisionsForCourt(divisionsForCourt);
        }

        applyServerFilter(courtId, "", searchText);
    };

    const handleCourtDivisionDropdownChange = (event) => {
        const divisionId = event.target.value;
        setSelectedCourtDivisionId(divisionId);
        applyServerFilter(selectedCourtId, divisionId, searchText);
    };

    const handleClearButtonClick = () => {
        setSearchText("");
        setSelectedCourtId("");
        setSelectedCourtDivisionId("");
        setCourtDivisionsForCourt([]);
        setMessage("");
        if (searchInputRef.current) searchInputRef.current.value = "";

        const alreadyBrowsingPage1 = currentPage === 1 && searchCriteria === null;
        setSearchCriteria(null);
        setCurrentPage(1);
        if (alreadyBrowsingPage1) {
            loadData(1, null);
        }
    };
 

    const handleOpenAdvancedSearch = () => {
        setIsAdvancedSearchOpen((prev) => !prev);
    };

      // AdvancedSearch hands back CRITERIA, not a result array. Setting criteria (and
    // resetting to page 1) triggers the effect, which queries the filtered set.
    const handleAffidavitRequestsSearchResults = (criteria) => {
        setSearchCriteria(criteria);
        setCurrentPage(1);
    };

    // ─── Cancel request ───────────────────────────────────────────────────────
    const handleCancel = async () => {
        try {
            setIsSubmitting(true);
            const url = `${process.env.REACT_APP_API_BASE_URL}/affidavit/cancel?DocumentId=${documentIdToCancel}`;
            await handleApiRequest(url, "PATCH");
            setIsSubmitting(false);
            hideDialog();
            toast.success("Request Cancelled Successfully");
            handleRefresh();
        } catch (error) {
            console.log("cancel error: ", error.message);
            setIsSubmitting(false);
            hideDialog();
            toast.error("An error occured while trying to update your request. Please try again later");
        }
    };

    // ─── Pagination ───────────────────────────────────────────────────────────
    // currentItems is the already-paged data from the server (then unsigned-filtered).
    // totalPages is derived from the server's totalCount.
    const currentItems = filteredAffidavitRequests;
    const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div className='container-content'>
            <div className="page-title">
                <p>Request List</p>
            </div>

            <div className="search-and-filter-container">
                <div className="filter-container">
                    <div className="search-box-container" style={searchBoxStyles.container}>
                        <input
                            type="text"
                            placeholder="Search by Document Id"
                            ref={searchInputRef}
                            className="search-input"
                            style={searchBoxStyles.input}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearchTextChange()}
                        />
                        <IconButton
                            iconProps={{ iconName: "Search" }}
                            style={searchBoxStyles.button}
                            onClick={handleSearchTextChange}
                        />
                    </div>

                    <div className="select-group">
                        <div className="select-item">
                            <label htmlFor="court-select">Filter by court name</label>
                            <select
                                id="court-select"
                                className="select-left"
                                value={selectedCourtId}
                                onChange={handleCourtDropdownChange}
                            >
                                <option value="" disabled hidden>All Courts</option>
                                {courts.length > 0 && courts.map((court) => (
                                    <option value={court.id} key={court.id}>{court.courtName}</option>
                                ))}
                            </select>
                        </div>
                        <div className="select-item">
                            <label htmlFor="division-select" disabled={!selectedCourtId}>
                                Filter by division
                            </label>
                            <select
                                id="division-select"
                                value={selectedCourtDivisionId}
                                onChange={handleCourtDivisionDropdownChange}
                                className="select-right"
                                disabled={!selectedCourtId}
                            >
                                <option value="" disabled hidden>All Divisions</option>
                                {courtDivisionsForCourt.map((division) => (
                                    <option value={division.id} key={division.id}>
                                        {division.courtDivisionName}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="button-group">
                        <button className="clear-button" onClick={handleClearButtonClick}>
                            <img src={clearicon} alt="Clear filters" />
                            Reset
                        </button>
                        <button className="clear-button" onClick={handleRefresh}>
                            <Refresh />
                            Refresh
                        </button>
                        <button className="mrl-btn mrl-btn-advanced" onClick={handleOpenAdvancedSearch}>
                            Advanced Search
                        </button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loader"></div>
            ) : filteredAffidavitRequests.length === 0 ? (
                <div className="emptytext">
                    <img src={emptybackground} alt="No records found" />
                    <p style={{ fontStyle: 'italic' }}>
                        {message || "An error occurred while processing your data. Please try again later."}
                    </p>
                </div>
            ) : (
                <div className='table-wrapper' style={{ height: tableHeight }}>
                    <table className="requests-table">
                        <thead>
                            <tr>
                                <th>Document ID</th>
                                <th>Date</th>
                                <th>Court Name</th>
                                <th>Court Division</th>
                                <th>Current Status</th>
                                <th>Actions</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.map((row, index) => (
                                <tr key={row.id ?? index} className="request-row">
                                    <td>{row.documentId}</td>
                                    <td>
                                        {new Date(row.createdAt)
                                            .toLocaleDateString('en-GB', {
                                                day: '2-digit',
                                                month: 'short',
                                                year: 'numeric'
                                            })
                                            .replace(/ /g, '-')}
                                    </td>
                                    <td>{row.court?.courtName}</td>
                                    <td>{row.courtDivision?.courtDivisionName}</td>
                                    <td>
                                        <span
                                            style={{
                                                backgroundColor:
                                                    row.status === 'Approved' ? '#2BA745' :
                                                    row.status === 'Rejected' ? '#721c24' :
                                                    row.status === 'Cancelled' ? '#808080' : '#FFA500',
                                                color: 'white',
                                                borderRadius: '4px',
                                                padding: '5px 10px',
                                                textAlign: 'center',
                                                width: '100px',
                                                display: 'inline-block',
                                            }}
                                        >
                                            {row.status}
                                        </span>
                                    </td>
                                    <td>
                                        <TooltipHost content="View Document">
                                            <IconButton
                                                iconProps={{ iconName: 'View' }}
                                                onClick={() => handleDownload(row.documentUrl)}
                                                styles={{
                                                    root: { backgroundColor: '#00000000', color: 'black', width: '20px', height: '20px' },
                                                    menuIcon: { fontSize: '18', marginLeft: '12px' }
                                                }}
                                            />
                                        </TooltipHost>
                                    </td>
                                    <td>
                                        <TooltipHost content="Cancel Request">
                                            <IconButton
                                                iconProps={{ iconName: 'Cancel' }}
                                                onClick={() => {
                                                    setDocumentIdToCancel(row.documentId);
                                                    showDialog();
                                                }}
                                                disabled={row.status !== 'Pending'}
                                                styles={{
                                                    root: { backgroundColor: '#00000000', color: 'black', width: '20px', height: '20px' },
                                                    menuIcon: { fontSize: '18', marginLeft: '12px' }
                                                }}
                                            />
                                        </TooltipHost>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal for Document Preview */}
            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button className="close-button" onClick={closeModal}>
                            <img src={clearicon} alt="Close" />
                            Close
                        </button>
                        <iframe
                            src={currentDocumentUrl}
                            style={{ width: '100%', height: '100%', marginTop: '10px' }}
                            title="Document Preview"
                        />
                    </div>
                </div>
            )}

            {/* Cancel confirmation dialog */}
            {isVisible && (
                <Dialog
                    hidden={!isVisible}
                    onDismiss={hideDialog}
                    styles={{
                        root: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
                        main: dialogStyles.main,
                        content: dialogStyles.content,
                    }}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: 'Cancel Affidavit Request',
                    }}
                    modalProps={{
                        styles: { main: dialogStyles },
                        isModeless: true,
                        dragOptions: isDraggable ? dragOptions : undefined,
                    }}
                >
                    <>
                        <Separator />
                        <Label>Are you sure you want to cancel this request?</Label>
                        <Separator />
                    </>
                    <DialogFooter>
                        <DefaultButton
                            onClick={hideDialog}
                            text="Cancel"
                            className={classNames.buttonSpacing}
                        />
                        <PrimaryButton
                            onClick={handleCancel}
                            className={classNames.customButtonColor}
                            text={isSubmitting ? 'Updating...' : 'Update'}
                            disabled={isSubmitting}
                        />
                    </DialogFooter>
                    {isSubmitting ? (
                        <Spinner size={SpinnerSize.small} label='Updating Request.....' />
                    ) : (
                        <div />
                    )}
                </Dialog>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={setCurrentPage}
                />
            )}

            {isAdvancedSearchOpen && (
                <AdvancedSearch
                    handleOpen={handleOpenAdvancedSearch}
                    courts={courts}
                    courtDivisions={courtDivisions}
                    returnSearchResults={handleAffidavitRequestsSearchResults}
                />
            )}
        </div>
    );
};

export default RequestsList;